import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Rocket, Sparkles, Settings, XCircle, Loader2 } from "lucide-react";
import { FounderPriceInline } from "@/components/FounderPriceBlock";

const HL = "bg-white border border-slate-200 rounded-2xl p-5";
const PLAN_LABEL = { fondateur: "PARTENAIRE FONDATEUR", visibilite: "PROXI VISIBILITÉ", premium: "PROXI PREMIUM", essentiel: "PROXI ESSENTIEL" };

export default function ProSubscription() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState({});
  const [portal, setPortal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/partner-config").then((r) => setCfg(r.data)).catch(() => setCfg({ plans: [] }));
    api.get("/pro/subscription").then((r) => setSub(r.data)).catch(() => setSub({ status: "none" }));
  }, []);

  if (!cfg || sub === null) return <div className="text-slate-500 p-8">Chargement…</div>;

  const currentPlan = sub?.status === "active" ? sub.plan_key : null;

  const startCheckout = async (planKey) => {
    setLoading((s) => ({ ...s, [planKey]: true }));
    try {
      const { data } = await api.post("/payments/checkout", {
        plan_key: planKey,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else toast.error("Impossible de créer la session de paiement.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors du paiement.");
    } finally {
      setLoading((s) => ({ ...s, [planKey]: false }));
    }
  };

  const openPortal = async () => {
    setPortal(true);
    try {
      const { data } = await api.post("/payments/portal");
      if (data.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'ouvrir le portail.");
    } finally { setPortal(false); }
  };

  const activateFounder = async () => {
    setLoading((s) => ({ ...s, fondateur: true }));
    try {
      await api.post("/founders/activate", {});
      toast.success("Votre place Partenaire Fondateur est activée — 0 € pendant 12 mois !");
      const r = await api.get("/pro/subscription");
      setSub(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Activation impossible.");
    } finally {
      setLoading((s) => ({ ...s, fondateur: false }));
    }
  };

  return (
    <div data-testid="pro-subscription-page">
      <h1 className="font-heading text-2xl md:text-3xl font-bold" data-testid="pro-subscription-title">Mon abonnement</h1>
      <p className="text-slate-500 text-sm mt-1">Votre formule et ses avantages. Sans engagement, sans carte bancaire.</p>

      {/* Statut actuel */}
      <div className={`${HL} mt-6`} data-testid="pro-subscription-current">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="w-11 h-11 rounded-xl bg-orange-50 text-[#E05638] grid place-items-center"><Sparkles size={20}/></div>
          <div className="flex-1 min-w-[240px]">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Formule actuelle</div>
            {currentPlan ? (
              <>
                <div className="font-heading text-xl font-extrabold text-slate-900">
                  {PLAN_LABEL[currentPlan] || currentPlan}
                </div>
                {sub?.founder && sub?.free ? (
                  <div className="text-xs text-emerald-700 font-semibold inline-flex items-center gap-1 mt-1"><CheckCircle2 size={12}/> Offerte — <FounderPriceInline /></div>
                ) : (
                  <div className="text-xs text-emerald-700 font-semibold inline-flex items-center gap-1 mt-1"><CheckCircle2 size={12}/> Abonnement actif</div>
                )}
                {sub?.founder && sub?.free_until && (
                  <div className="mt-1 text-xs text-slate-500">Gratuit jusqu'au {String(sub.free_until).slice(0, 10)}</div>
                )}
                {sub?.cancel_at_period_end && (
                  <div className="mt-1 text-xs text-amber-700">Résiliation programmée à la fin de la période.</div>
                )}
              </>
            ) : (
              <>
                <div className="font-heading text-xl font-extrabold text-slate-900">PRÉSENCE (gratuite)</div>
                <div className="text-xs text-slate-500 mt-1">Activez l'offre Partenaire Fondateur (<FounderPriceInline />) pour publier vos offres sans limite.</div>
              </>
            )}
          </div>
          {currentPlan && !sub?.free && sub?.stripe_customer_id && (
            <Button variant="outline" onClick={openPortal} disabled={portal} data-testid="pro-portal-btn">
              {portal ? <Loader2 size={14} className="animate-spin"/> : <Settings size={14} className="mr-1"/>}
              Gérer / Résilier
            </Button>
          )}
        </div>
      </div>

      {/* Plans — toutes les formules sur une ligne (jusqu'à 4) */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {(cfg.plans || []).filter((p) => ["fondateur", "premium"].includes(p.key)).map((p) => {
          const greyed = p.disabled || p.key === "premium";
          const isFounderFree = p.key === "fondateur";
          const isCurrent = currentPlan === p.key;
          return (
            <div key={p.key} className={`relative rounded-2xl border p-4 flex flex-col bg-white ${p.highlight ? "border-[#E05638] shadow-md ring-1 ring-[#E05638]/20" : "border-slate-200"}`}
                 data-testid={`pro-plan-${p.key}`}>
              {p.badge && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-widest whitespace-nowrap ${p.highlight ? "bg-[#E05638] text-white" : "bg-slate-900 text-white"}`}>{p.badge}</div>
              )}
              <div className="font-heading text-base font-extrabold text-slate-900">{p.name}</div>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="font-heading text-2xl font-extrabold text-slate-900">{p.price_display}</span>
                <span className="text-slate-500 text-[11px]">{p.period}</span>
              </div>
              <p className="mt-1 text-[11px] text-slate-600 line-clamp-2">{p.tagline}</p>
              {p.trial_note && (
                <div className="mt-1.5 text-[10px] font-bold text-emerald-700">{p.trial_note}</div>
              )}
              {p.free_features ? (
                <div className="mt-2.5 flex-1 space-y-2">
                  <ul className="space-y-1 text-[11px] text-slate-700 leading-tight">
                    <li className="text-[9px] font-extrabold uppercase tracking-wide text-emerald-700">Gratuit, pour toujours</li>
                    {(p.free_features || []).slice(0, 6).map((f) => (
                      <li key={f} className="flex gap-1"><CheckCircle2 size={11} className="text-emerald-600 mt-0.5 shrink-0"/> <span>{f}</span></li>
                    ))}
                    {(p.free_features || []).length > 6 && (
                      <li className="text-slate-400">… et {(p.free_features).length - 6} autres</li>
                    )}
                  </ul>
                  {(p.trial_features || []).length > 0 && (
                    <div className="rounded-lg bg-amber-50 border border-amber-200 p-2">
                      <div className="text-[9px] font-extrabold uppercase tracking-wide text-amber-700 flex items-center gap-1"><Sparkles size={10}/> Offert en essai 30 jours</div>
                      <ul className="mt-1 space-y-1 text-[11px] text-slate-700 leading-tight">
                        {(p.trial_features || []).map((f) => (
                          <li key={f} className="flex gap-1"><Sparkles size={10} className="text-amber-600 mt-0.5 shrink-0"/> <span>{f}</span></li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <ul className="mt-2.5 space-y-1 text-[11px] text-slate-700 flex-1 leading-tight">
                  {(p.features || []).slice(0, 6).map((f) => (
                    <li key={f} className="flex gap-1"><CheckCircle2 size={11} className="text-emerald-600 mt-0.5 shrink-0"/> <span>{f}</span></li>
                  ))}
                  {(p.features || []).length > 6 && (
                    <li className="text-slate-400">… et {(p.features).length - 6} autres</li>
                  )}
                </ul>
              )}
              {isCurrent ? (
                <Button disabled className="w-full mt-3 h-9 text-xs" variant="outline" data-testid={`pro-plan-current-${p.key}`}>
                  <CheckCircle2 size={12} className="mr-1 text-emerald-600"/> Formule actuelle
                </Button>
              ) : greyed ? (
                <Button disabled className="w-full mt-3 h-9 text-xs" variant="outline" data-testid={`pro-plan-cta-${p.key}`}>
                  Bientôt disponible
                </Button>
              ) : isFounderFree ? (
                <Button className="w-full mt-3 h-9 text-xs btn-shine text-white"
                        disabled={!!loading[p.key]}
                        onClick={activateFounder}
                        data-testid={`pro-plan-cta-${p.key}`}>
                  {loading[p.key] ? <Loader2 size={12} className="animate-spin mr-1"/> : <Rocket size={12} className="mr-1"/>}
                  {loading[p.key] ? "Activation…" : "Activer — 0 €"}
                </Button>
              ) : (
                <Button className={`w-full mt-3 h-9 text-xs ${p.highlight ? "btn-shine text-white" : ""}`}
                        variant={p.highlight ? "default" : "outline"}
                        disabled={!!loading[p.key]}
                        onClick={() => startCheckout(p.key)}
                        data-testid={`pro-plan-cta-${p.key}`}>
                  {loading[p.key] ? <Loader2 size={12} className="animate-spin mr-1"/> : <Rocket size={12} className="mr-1"/>}
                  {loading[p.key] ? "Redirection…" : "Passer à cette formule"}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <p className="mt-4 text-xs text-slate-500">
        Offre Partenaire Fondateur : <FounderPriceInline />, sans carte bancaire, sans engagement. TVA non applicable, art. 293 B du CGI.
      </p>
    </div>
  );
}
